import React, { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ChevronLeft,
  Users,
  Target,
  CheckCircle2,
  Trophy,
  TrendingUp,
  AlertTriangle,
  Sparkles,
  Pencil,
  Play,
} from "lucide-react";

function fmtDate(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short", day: "2-digit",
      hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

function KpiCard({ icon: Icon, label, value, accent = "#1B2E7A", suffix }) {
  return (
    <div className="bg-white border border-[#D1D5DB] rounded-lg p-4">
      <div className="flex items-center gap-2 mb-2">
        <div className="h-7 w-7 rounded-md flex items-center justify-center" style={{ backgroundColor: `${accent}1A` }}>
          <Icon className="h-3.5 w-3.5" strokeWidth={1.8} style={{ color: accent }} />
        </div>
        <p className="text-[10px] uppercase tracking-[0.18em] text-[#6B7280] font-medium">{label}</p>
      </div>
      <p className="font-heading text-3xl text-[#1A1A1A] leading-none">
        {value}
        {suffix && <span className="text-base text-[#6B7280] font-sans ml-1">{suffix}</span>}
      </p>
    </div>
  );
}

/**
 * Per-scenario analytics dashboard (admin only).
 *
 * Shows 5 KPIs, per-node option distribution bars, list of most-missed
 * incorrect picks, and recent attempts with user names.
 */
export default function AdminScenarioAnalytics() {
  const { id } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(`/admin/scenarios/${id}/analytics`);
        if (!cancelled) setData(r.data);
      } catch (_) {}
      finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [id]);

  if (loading) return <div className="text-[#374151]">Loading analytics...</div>;
  if (!data) return <div className="text-[#374151]">Scenario not found.</div>;

  const k = data.kpis;
  const hasData = k.total_attempts > 0;

  return (
    <div className="space-y-6" data-testid="scenario-analytics-page">
      <div>
        <Link to="/admin/scenarios" className="inline-flex items-center gap-1.5 text-xs text-[#6B7280] hover:text-[#1B2E7A] mb-3">
          <ChevronLeft className="h-3.5 w-3.5" /> Back to scenarios
        </Link>
        <div className="flex items-end justify-between gap-3 flex-wrap">
          <div className="pl-4 border-l-4 border-[#F4B81F]">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#F4B81F] font-bold mb-1">
              <Sparkles className="h-3 w-3 inline mr-1" /> Scenario analytics
            </p>
            <h1 className="font-heading text-3xl text-[#1A1A1A] leading-tight">{data.scenario_title}</h1>
          </div>
          <div className="flex items-center gap-2">
            <Link
              to={`/scenarios/${id}`}
              target="_blank"
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-[#1B2E7A] border border-[#1B2E7A] hover:bg-[#EFF6FF]"
            >
              <Play className="h-4 w-4" /> Test
            </Link>
            <Link
              to={`/admin/scenarios/${id}`}
              className="inline-flex items-center gap-1.5 px-3 py-2 rounded-md text-sm text-[#374151] border border-[#D1D5DB] hover:bg-[#F3F4F6]"
            >
              <Pencil className="h-4 w-4" /> Edit
            </Link>
          </div>
        </div>
      </div>

      {!hasData ? (
        <div className="text-center py-16 bg-white border border-[#D1D5DB] rounded-md">
          <TrendingUp className="h-10 w-10 text-[#6B7280] mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-[#374151]">No attempts yet. Analytics will populate once your team starts practicing.</p>
        </div>
      ) : (
        <>
          {/* KPIs */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="analytics-kpis">
            <KpiCard icon={Trophy} label="Total attempts" value={k.total_attempts} />
            <KpiCard icon={Users} label="Unique users" value={k.unique_users} accent="#7C3AED" />
            <KpiCard icon={Target} label="Avg score" value={k.avg_score} accent="#F4B81F" />
            <KpiCard icon={CheckCircle2} label="Avg correct" value={k.avg_correct_pct} suffix="%" accent="#16A34A" />
            <KpiCard icon={TrendingUp} label="Pass rate" value={k.pass_rate} suffix="%" accent="#DC2626" />
          </div>

          {/* Top missed picks */}
          {data.top_missed.length > 0 && (
            <div className="bg-[#FEF3C7] border border-[#F4B81F] rounded-xl p-5" data-testid="analytics-top-missed">
              <div className="flex items-center gap-2 mb-3">
                <AlertTriangle className="h-4 w-4 text-[#854D0E]" strokeWidth={2} />
                <p className="text-[10px] uppercase tracking-[0.2em] text-[#854D0E] font-bold">Most common mistakes</p>
              </div>
              <p className="text-xs text-[#854D0E] mb-3">
                Incorrect picks your team keeps making. Worth a group training session.
              </p>
              <div className="space-y-2">
                {data.top_missed.map((m, idx) => (
                  <div key={idx} className="bg-white border border-[#F4B81F] rounded-md p-3">
                    <div className="flex items-center justify-between gap-3 mb-1">
                      <p className="text-sm text-[#1A1A1A] font-medium flex-1">{m.option_text}</p>
                      <span className="text-xs font-bold text-[#854D0E] whitespace-nowrap">
                        {m.pick_count}× ({m.pick_pct}%)
                      </span>
                    </div>
                    <p className="text-[11px] text-[#6B7280] italic">In: "{m.node_prompt}..."</p>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Per-node distribution */}
          <div data-testid="analytics-per-node">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold mb-2">
              How your team answers at each step
            </p>
            <div className="space-y-4">
              {data.per_node.map((n) => (
                <div key={n.node_id} className="bg-white border border-[#D1D5DB] rounded-lg p-4">
                  <code className="text-[10px] text-[#1B2E7A] font-mono">{n.node_id}</code>
                  <p className="text-sm text-[#1A1A1A] italic mt-1 mb-3">"{n.prompt}"</p>
                  {n.total_picks === 0 ? (
                    <p className="text-xs text-[#9CA3AF] italic">No picks reached this node yet.</p>
                  ) : (
                    <div className="space-y-2">
                      {n.options.map((o, idx) => (
                        <div key={o.id}>
                          <div className="flex items-center gap-2 mb-1">
                            <span className="h-5 w-5 rounded-full bg-[#F3F4F6] text-[#374151] text-[10px] flex items-center justify-center font-bold">
                              {String.fromCharCode(65 + idx)}
                            </span>
                            {o.is_correct && (
                              <CheckCircle2 className="h-3.5 w-3.5 text-[#16A34A]" strokeWidth={2.2} />
                            )}
                            <p className="text-sm text-[#1A1A1A] flex-1">{o.text}</p>
                            <span className="text-xs font-semibold text-[#1A1A1A] whitespace-nowrap">
                              {o.pick_count} ({o.pick_pct}%)
                            </span>
                          </div>
                          <div className="h-1.5 bg-[#F3F4F6] rounded-full overflow-hidden">
                            <div
                              className="h-full transition-all duration-500"
                              style={{
                                width: `${o.pick_pct}%`,
                                backgroundColor: o.is_correct ? "#16A34A" : "#DC2626",
                              }}
                            />
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Recent attempts */}
          <div data-testid="analytics-recent">
            <p className="text-[10px] uppercase tracking-[0.2em] text-[#6B7280] font-bold mb-2">
              Recent attempts (last 20)
            </p>
            <div className="bg-white border border-[#D1D5DB] rounded-md overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-[#F3F4F6] text-[#374151] border-b-2 border-[#D1D5DB]">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium">Salesperson</th>
                    <th className="text-left px-4 py-2 font-medium">Score</th>
                    <th className="text-left px-4 py-2 font-medium">Correct</th>
                    <th className="text-left px-4 py-2 font-medium">Outcome</th>
                    <th className="text-left px-4 py-2 font-medium">When</th>
                  </tr>
                </thead>
                <tbody>
                  {data.recent.map((a, idx) => (
                    <tr key={a.id} className={idx % 2 === 0 ? "bg-white" : "bg-[#F9FAFB]"}>
                      <td className="px-4 py-2.5 text-[#1A1A1A] font-medium">{a.user_name}</td>
                      <td className="px-4 py-2.5">
                        <span className="font-mono">{a.score}</span>
                      </td>
                      <td className="px-4 py-2.5">
                        <span className={a.correct_picks >= 1 ? "text-[#16A34A] font-medium" : "text-[#DC2626]"}>
                          {a.correct_picks}/{a.total_picks}
                        </span>
                      </td>
                      <td className="px-4 py-2.5 text-[#374151] text-xs">{a.ending_label || "—"}</td>
                      <td className="px-4 py-2.5 text-[#6B7280] text-xs whitespace-nowrap">{fmtDate(a.finished_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}
